/* eslint-disable no-new */
import type { RoleUser } from 'src/auth/types';

import React, { lazy, Suspense, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { Outlet, useLocation } from 'react-router-dom';

import { useFcm } from 'src/hooks/useFcm';

import { QUERY_KEY } from 'src/utils/queryKeys';

import { CONFIG } from 'src/config-global';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loadingScreen';

import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';
import { useAuthContext } from 'src/auth/hooks';

import axiosInstance from 'src/utils/axios';
import { authRoutes } from './auth';
import { clientRoutes, manageRoutes, teacherRoutes } from './config';

const IndexPage = lazy(() => import('src/pages/dashboard'));
const DashboardClient = lazy(() => import('src/pages/dashboard/clients/dashboard'));
const ZoomMeetting = lazy(() => import('src/pages/zoom'));
const LeaveZoomMeetting = lazy(() => import('src/pages/zoom/leave'));
const ExamPage = lazy(() => import('src/pages/exam'));
const ExamRealtimePage = lazy(() => import('src/pages/exam/realtime'));
const LearningViewer = lazy(() => import('src/pages/dashboard/clients/learningViewer'));
const TESTSCROM = lazy(() => import('src/pages/dashboard/clients/learningViewer/test'));
const SurveyViewer = lazy(() => import('src/pages/dashboard/clients/survey'));
const FeedbackSurveyViewer = lazy(() => import('src/pages/dashboard/clients/survey/feedback'));
const DocumentViewer = lazy(() => import('src/pages/dashboard/clients/documents'));
const SurveyResultViewer = lazy(() => import('src/pages/dashboard/clients/survey/result'));

const CourseAnalyticsPage = lazy(
  () => import('src/pages/dashboard/administrators/courseAnalytics')
);
const FileAnalyticsPage = lazy(() => import('src/pages/dashboard/administrators/fileAnalytics'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));

const FileMangePage = lazy(() => import('src/pages/dashboard/manage/fileManage'));

const EmailSettingPage = lazy(() => import('src/pages/dashboard/administrators/settings/email'));
const RegulationPage = lazy(() => import('src/pages/dashboard/administrators/settings/regulation'));
const SubdomainPage = lazy(() => import('src/pages/dashboard/administrators/settings/subdomain'));

const changeOfflineStatus = async () => {
  await axiosInstance.get(`/auth/offline`);
};
const changeOnlineStatus = async () => {
  await axiosInstance.get(`/auth/online`);
};

const LayoutContent = ({
  acceptRole,
  hasLayout = true,
}: {
  acceptRole: RoleUser[];
  hasLayout?: boolean;
}) => {
  const { user } = useAuthContext();
  const { pathname } = useLocation();
  // const { openSession, closeSession } = useLoginHistory(user?.id);
  const queryClient = useQueryClient();

  // useEffect(() => {
  //   if (user?.id) {
  //     openSession();
  //   }
  // }, [user?.id, openSession]);

  const send = (path: string, payload: any) => {
    const url = `${CONFIG.site.serverUrl}${path}`;

    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user?.accessToken}`,
      },
    });
  };
  useEffect(() => {
    const handleUnload = () => {
      send('/auth/offline', {}) || navigator.sendBeacon('/noop', new Blob()); // no-op
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        send('/auth/offline', {});
        changeOfflineStatus();
      } else {
        changeOnlineStatus();
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const { lastMsg } = useFcm(user?.id);

  const handledIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!lastMsg) return;
    const id = lastMsg.messageId ?? JSON.stringify(lastMsg);
    if (!id) return;
    if (handledIdRef.current === id) return;
    handledIdRef.current = id;
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.MESSAGE.CONVERSATIONS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.MESSAGE.CONVERSATION] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.NOTIFICATION.MY_NOTIFICATIONS] });
  }, [lastMsg, queryClient]);

  return (
    user && (
      <RoleBasedGuard hasContent currentRole={user.role} acceptRoles={acceptRole} sx={{ py: 10 }}>
        {hasLayout ? (
          <DashboardLayout role={user.role}>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        ) : (
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        )}
        {/* {!showAIAssistant && <AIAssistant />} */}
      </RoleBasedGuard>
    )
  );
};

export const dashboardRoutes = [
  ...authRoutes,
  {
    path: 'administrator',
    element: CONFIG.auth.skip ? (
      <LayoutContent acceptRole={['administrator']} />
    ) : (
      <AuthGuard>
        <LayoutContent acceptRole={['administrator']} />
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'analytics-course', element: <CourseAnalyticsPage /> },
      { path: 'bunny-analytics', element: <FileAnalyticsPage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      {
        path: 'file-manager',
        children: [{ element: <FileMangePage />, index: true }],
      },

      {
        path: 'setting',
        children: [
          { path: 'email-setting', element: <EmailSettingPage /> },
          { path: 'regulation', element: <RegulationPage /> },
          { path: 'sub-domain', element: <SubdomainPage /> },
        ],
      },
      ...manageRoutes,
    ],
  },
  {
    path: 'teacher',
    element: CONFIG.auth.skip ? (
      <LayoutContent acceptRole={['teacher']} />
    ) : (
      <AuthGuard>
        <LayoutContent acceptRole={['teacher']} />
      </AuthGuard>
    ),
    children: [{ element: <IndexPage />, index: true }, ...teacherRoutes],
  },
  {
    path: 'manage',
    element: CONFIG.auth.skip ? (
      <LayoutContent acceptRole={['manage']} />
    ) : (
      <AuthGuard>
        <LayoutContent acceptRole={['manage']} />
      </AuthGuard>
    ),
    children: [{ index: true, element: <IndexPage /> }, ...manageRoutes],
  },
  {
    path: 'learning/:slugCourse',
    element: CONFIG.auth.skip ? (
      <LayoutContent
        acceptRole={['client', 'administrator', 'teacher', 'manage']}
        hasLayout={false}
      />
    ) : (
      <AuthGuard>
        <LayoutContent
          acceptRole={['client', 'administrator', 'teacher', 'manage']}
          hasLayout={false}
        />
      </AuthGuard>
    ),
    children: [{ element: <LearningViewer />, index: true }],
  },
  {
    path: 'learning/:slugCourse',
    element: CONFIG.auth.skip ? (
      <LayoutContent
        acceptRole={['client', 'administrator', 'teacher', 'manage']}
        hasLayout={false}
      />
    ) : (
      <AuthGuard>
        <LayoutContent
          acceptRole={['client', 'administrator', 'teacher', 'manage']}
          hasLayout={false}
        />
      </AuthGuard>
    ),
    children: [{ element: <LearningViewer />, index: true }],
  },
  {
    path: 'test-scorm',
    element: CONFIG.auth.skip ? (
      <LayoutContent
        acceptRole={['client', 'administrator', 'teacher', 'manage']}
        hasLayout={false}
      />
    ) : (
      <AuthGuard>
        <LayoutContent
          acceptRole={['client', 'administrator', 'teacher', 'manage']}
          hasLayout={false}
        />
      </AuthGuard>
    ),
    children: [{ element: <TESTSCROM />, index: true }],
  },
  {
    path: 'survey',
    element: CONFIG.auth.skip ? (
      <LayoutContent
        acceptRole={['client', 'administrator', 'teacher', 'manage']}
        hasLayout={false}
      />
    ) : (
      <AuthGuard>
        <LayoutContent
          acceptRole={['client', 'administrator', 'teacher', 'manage']}
          hasLayout={false}
        />
      </AuthGuard>
    ),
    children: [
      { path: ':slug', element: <SurveyViewer />, index: true },
      { path: 'feedback/:slug', element: <FeedbackSurveyViewer /> },
      { path: 'result/:slug', element: <SurveyResultViewer /> },
    ],
  },
  {
    path: 'zoom-meeting',
    element: <Outlet />,
    children: [
      { element: <ZoomMeetting />, index: true },
      { path: 'leave', element: <LeaveZoomMeetting /> },
    ],
  },
  {
    path: 'exam',
    element: <Outlet />,
    children: [
      { element: <ExamPage />, index: true },
      { path: 'realtime', element: <ExamRealtimePage /> },
    ],
  },
  {
    path: 'file',
    element: CONFIG.auth.skip ? (
      <LayoutContent
        acceptRole={['client', 'administrator', 'teacher', 'manage']}
        hasLayout={false}
      />
    ) : (
      <AuthGuard>
        <LayoutContent
          acceptRole={['client', 'administrator', 'teacher', 'manage']}
          hasLayout={false}
        />
      </AuthGuard>
    ),
    children: [{ path: 'viewer', element: <DocumentViewer />, index: true }],
  },
  {
    path: 'client',
    element: CONFIG.auth.skip ? (
      <LayoutContent acceptRole={['client']} />
    ) : (
      <AuthGuard>
        <LayoutContent acceptRole={['client']} />
      </AuthGuard>
    ),
    children: [{ element: <DashboardClient />, index: true }, ...clientRoutes],
  },
];
